import { ToolBase, ToolPointer } from './tool';
import {
  CustomFurnitureData,
  FurnitureTemplate,
  isUserTemplate,
  orientParts,
  placeTemplate,
  templateFootprint
} from '../furniture/furniture';
import { AXIS_Z, Vec3, dot, projectPointOnPlane, sub, vec } from '../core/vec';
import { formatLength } from '../core/units';

/** Möbel-Vorlage: Katalogstück oder eigenes (aus gezeichneter Gruppe gespeichertes) Möbel. */
export type FurnitureChoice = FurnitureTemplate | CustomFurnitureData;

/**
 * Möbel stellen: ein Möbelstück aus der Bibliothek folgt als Geistervorschau dem
 * Cursor auf dem Boden; ein Klick stellt es als Gruppe. Pfeiltasten drehen um 90°,
 * das Werkzeug bleibt aktiv, damit mehrere Möbel hintereinander gestellt werden können.
 */
export class PlaceTool extends ToolBase {
  readonly id = 'place';
  readonly label = 'Möbel stellen';
  readonly shortcut = '';
  readonly cursor = 'crosshair';
  readonly hint = 'Klicken: Möbel stellen · Pfeiltasten ←/→: um 90° drehen · Esc: fertig';

  private tpl: FurnitureChoice | null = null;
  private quarterTurns = 0;
  private cursor3d: Vec3 | null = null;

  setTemplate(tpl: FurnitureChoice): void {
    this.tpl = tpl;
    this.quarterTurns = 0;
    this.cursor3d = null;
  }

  currentTemplate(): FurnitureChoice | null {
    return this.tpl;
  }

  enter(): void {
    this.quarterTurns = 0;
    this.cursor3d = null;
  }

  exit(): void {
    this.ctx.preview.clear();
    this.tpl = null;
  }

  keyDown(e: KeyboardEvent): void {
    if (e.key === 'ArrowRight') this.quarterTurns++;
    else if (e.key === 'ArrowLeft') this.quarterTurns--;
    else return;
    const deg = ((this.quarterTurns * 90) % 360 + 360) % 360;
    this.ctx.ui.setStatus(`Gedreht auf ${deg}° · Klicken: stellen · Esc: fertig`);
    this.drawGhost();
    this.ctx.requestUpdate();
  }

  pointerMove(e: ToolPointer): void {
    const hit = projectPointOnPlane(e.ray.origin, e.ray.dir, vec(), AXIS_Z);
    this.cursor3d = hit && this.inFront(e.ray, hit) ? hit : null;
    this.drawGhost();
    this.ctx.requestUpdate();
  }

  pointerDown(e: ToolPointer): void {
    if (e.button !== 0) return;
    this.pointerMove(e);
    this.stamp();
  }

  cancel(): void {
    this.cursor3d = null;
    this.ctx.preview.clear();
    this.ctx.ui.setStatus(null);
  }

  private inFront(ray: { origin: Vec3; dir: Vec3 }, hit: Vec3): boolean {
    return dot(sub(hit, ray.origin), ray.dir) > 0;
  }

  private stamp(): void {
    const tpl = this.tpl;
    if (!tpl || !this.cursor3d) return;
    const { length, depth } = templateFootprint(tpl);
    const origin = vec(this.cursor3d.x - length / 2, this.cursor3d.y - depth / 2, 0);
    this.ctx.beginEdit();
    const gid = placeTemplate(this.ctx.model, tpl, origin, this.quarterTurns);
    if (gid !== null) {
      const ent = this.ctx.model.entitiesInGroup(gid);
      this.ctx.selection.set(ent.edges, ent.faces);
      this.ctx.ui.setStatus('Gestellt · Verschieben (M) oder Maße im Panel anpassen · Esc beendet das Stellen');
    }
    this.ctx.requestUpdate();
  }

  private drawGhost(): void {
    this.ctx.preview.clear();
    const tpl = this.tpl;
    if (!tpl || !this.cursor3d) return;
    const { length, depth } = templateFootprint(tpl);
    const origin = vec(this.cursor3d.x - length / 2, this.cursor3d.y - depth / 2, 0);

    if (isUserTemplate(tpl)) {
      // Drahtgitter-Geist: alle Flächenringe der gespeicherten Geometrie
      const k = ((this.quarterTurns % 4) + 4) % 4;
      let verts = tpl.vertices;
      let len = tpl.length;
      let dep = tpl.depth;
      for (let i = 0; i < k; i++) {
        verts = verts.map(([x, y, z]) => [dep - y, x, z] as [number, number, number]);
        const nl = dep;
        dep = len;
        len = nl;
      }
      const abs = (i: number): Vec3 => vec(origin.x + verts[i]![0]!, origin.y + verts[i]![1]!, origin.z + verts[i]![2]!);
      for (const [outer, holes] of tpl.faces) {
        for (const ring of [outer, ...holes]) {
          this.ctx.preview.polyline(ring.map(abs), 0x55606a, true);
        }
      }
    } else {
      for (const part of orientParts(tpl, this.quarterTurns).parts) {
        const min = vec(origin.x + part.dx, origin.y + part.dy, origin.z + part.dz);
        const max = vec(min.x + part.w, min.y + part.d, min.z + part.h);
        this.ctx.preview.box(min, max, hexToNumber(part.color));
      }
    }

    // Fußabdruck als Umrandung auf dem Boden
    const foot = [
      vec(origin.x, origin.y, 0.004),
      vec(origin.x + length, origin.y, 0.004),
      vec(origin.x + length, origin.y + depth, 0.004),
      vec(origin.x, origin.y + depth, 0.004)
    ];
    this.ctx.preview.polyline(foot, 0xe0552c, true);
    this.ctx.ui.setStatus(
      `${tpl.name} · ${formatLength(length, this.ctx.units())} × ${formatLength(depth, this.ctx.units())} · Klicken: stellen · Esc: fertig`
    );
  }
}

function hexToNumber(hex: string): number {
  return parseInt(hex.replace('#', ''), 16);
}
